import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import userProfileService from '../../services/userProfileService';
import venueDashboardApiService from '../../services/venueDashboardApi';
import { fetchUserVenueBriefs, getActivePendingBrief } from '../../services/venueBriefService';
import OrganizerProfileCompletion from './OrganizerProfileCompletion';
import {
  computeBriefCountdown,
  computeProfileCompletion,
  formatOrganizerCurrency,
  formatOrganizerDate,
  formatOrganizerTimeRange,
  getOrganizerBookingBadge,
  getOrganizerPaymentLabel,
} from './organizerUtils';
import {
  bookingNeedsOrganizerPayment,
  canShowOrganizerBookingPricing,
} from '../../utils/bookingPayment';
import { IconCalendar, IconClock, IconTimer, IconUsers } from './OrganizerIcons';

const parseMustHaves = (brief) => {
  const raw = brief?.must_haves || brief?.must_haves_list || brief?.mustHaves;
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (typeof raw === 'string' && raw.trim()) {
    return raw.split(',').map((item) => item.trim()).filter(Boolean);
  }
  return [];
};

const PendingBriefCard = ({ brief, countdown }) => {
  const mustHaves = parseMustHaves(brief);

  return (
    <article className="org-home-card org-home-card--wide">
      <div className="org-home-card-header">
        <div className="org-home-card-header-title">
          <IconTimer />
          <span>Pending Requests</span>
        </div>
        {countdown ? (
          <p className="org-home-card-countdown">
            Confirming in <strong>{countdown}</strong>
          </p>
        ) : null}
      </div>

      <div className="org-home-card-body">
        <div className="org-home-event-title">
          <span>{brief.event_type || 'Event'}</span>
          <span className="org-home-event-at">@</span>
          <span>{brief.target_area || 'London'}</span>
        </div>

        <div className="org-home-meta-row">
          <IconUsers />
          <span>{brief.number_of_guests ? `${brief.number_of_guests} guests` : 'Guests TBD'}</span>
        </div>

        <div className="org-home-meta-row">
          <IconCalendar />
          <span>{brief.first_date ? formatOrganizerDate(brief.first_date) : 'Date TBD'}</span>
          {brief.date_flexibility ? (
            <>
              <span className="org-home-meta-sep">/</span>
              <span>{brief.date_flexibility}</span>
            </>
          ) : null}
          {brief.booking_frequency ? (
            <>
              <span className="org-home-meta-sep">/</span>
              <span>{brief.booking_frequency}</span>
            </>
          ) : null}
        </div>

        <div className="org-home-meta-row">
          <IconClock />
          <span>{brief.booking_time_slot || brief.time_slot || 'Time TBD'}</span>
          {brief.duration_needed ? (
            <>
              <span className="org-home-meta-sep">/</span>
              <span>{brief.duration_needed}</span>
            </>
          ) : null}
        </div>

        <hr className="org-home-divider" />

        <div className="org-home-detail-grid">
          {brief.vibes || brief.vibe ? (
            <div className="org-home-detail-item">
              <span className="org-home-detail-label">Vibe</span>
              <span className="org-home-detail-value">{brief.vibes || brief.vibe}</span>
            </div>
          ) : null}
          {brief.space_types || brief.space_type ? (
            <div className="org-home-detail-item">
              <span className="org-home-detail-label">Type of space</span>
              <span className="org-home-detail-value">{brief.space_types || brief.space_type}</span>
            </div>
          ) : null}
          {brief.budget_range ? (
            <div className="org-home-detail-item">
              <span className="org-home-detail-label">Hire budget</span>
              <span className="org-home-detail-value">{brief.budget_range}</span>
            </div>
          ) : null}
        </div>

        {mustHaves.length > 0 ? (
          <div className="org-home-must-haves">
            <span className="org-home-detail-label">Must haves</span>
            <div className="org-home-tags">
              {mustHaves.map((item) => (
                <span key={item} className="org-home-tag">
                  {item}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {brief.extra_notes || brief.additional_notes ? (
          <div className="org-home-notes">
            <span className="org-home-detail-label">Any other information</span>
            <p>{brief.extra_notes || brief.additional_notes}</p>
          </div>
        ) : null}
      </div>

      <div className="org-home-card-footer">
        <div className="org-home-outreach">
          <div>
            <p className="org-home-outreach-title">Reaching out to venues now</p>
            <p className="org-home-outreach-text">
              We&apos;re contacting best matches for you. We&apos;ll update you as soon as we have a
              response. <strong>Usually within 24 hours</strong>
            </p>
          </div>
        </div>
      </div>
    </article>
  );
};

const FeaturedBookingCard = ({ booking, venueImage, paymentLabel }) => {
  const badge = getOrganizerBookingBadge(booking);
  const bookingId = booking.id || booking.booking_reference;
  const needsPayment = bookingNeedsOrganizerPayment(booking);
  const venueApproved = canShowOrganizerBookingPricing(booking);

  return (
    <article className="org-home-card org-home-card--wide">
      <div className="org-home-booking-feature">
        {venueImage ? (
          <img src={venueImage} alt={booking.venueName || 'Venue'} className="org-home-booking-image" />
        ) : (
          <div className="org-home-booking-image org-home-booking-image--placeholder" />
        )}

        <div className="org-home-booking-content">
          <div className="org-home-booking-top">
            <div>
              <h2 className="org-home-booking-venue">{booking.venueName || 'Venue'}</h2>
              <p className="org-home-booking-activity">
                {booking.activity || booking.eventType || 'Event booking'}
              </p>
            </div>
            <span className={`org-badge org-badge--${badge.variant}`}>{badge.label}</span>
          </div>

          <div className="org-home-meta-row">
            <IconUsers />
            <span>{booking.headcount || booking.attendees || '—'} guests</span>
          </div>
          <div className="org-home-meta-row">
            <IconCalendar />
            <span>
              {formatOrganizerDate(booking.date || booking.bookingDate)},{' '}
              {formatOrganizerTimeRange(booking.startTime, booking.endTime)}
            </span>
          </div>

          <div className="org-home-booking-footer">
            <div className="org-home-booking-price">
              <span className="org-home-booking-price-label">Total</span>
              <span className="org-home-booking-price-value">
                {venueApproved
                  ? formatOrganizerCurrency(booking.totalPrice || booking.amountPending)
                  : 'TBC'}
              </span>
            </div>
            <div className="org-home-booking-actions">
              <span className={`org-payment-pill org-payment-pill--${paymentLabel.variant}`}>
                {paymentLabel.label}
              </span>
              {bookingId && needsPayment ? (
                <Link
                  to={`/profile/bookings/${bookingId}?pay=stripe`}
                  className="org-btn org-btn--primary org-btn--compact"
                >
                  Pay now
                </Link>
              ) : (
                <Link to={`/profile/bookings/${bookingId}`} className="org-btn org-btn--secondary org-btn--compact">
                  View details
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

const OrganizerHome = () => {
  const { currentUser, userProfile } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [pendingBrief, setPendingBrief] = useState(null);
  const [venueImage, setVenueImage] = useState('');
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(null);

  const firstName =
    userProfile?.first_name ||
    userProfile?.firstName ||
    (currentUser?.displayName ? currentUser.displayName.split(' ')[0] : null) ||
    'there';

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!currentUser) return;
      setLoading(true);

      try {
        const jwtToken = localStorage.getItem('access_token');
        if (jwtToken) {
          userProfileService.setAuthToken(jwtToken);
          userProfileService.setUser(currentUser);
        }

        const email = userProfile?.email || currentUser?.email;
        const [userBookings, briefs] = await Promise.all([
          userProfileService.getUserBookings().catch(() => []),
          fetchUserVenueBriefs(email),
        ]);

        if (cancelled) return;

        const nextBookings = userBookings || [];
        setBookings(nextBookings);
        setPendingBrief(getActivePendingBrief(briefs));

        const featured =
          nextBookings.find((b) => {
            const status = String(b.status || '').toLowerCase();
            return status !== 'cancelled' && status !== 'completed';
          }) || null;

        if (featured?.venueId) {
          try {
            const venue = await venueDashboardApiService.getVenueDetails(featured.venueId);
            const imageUrl =
              venue?.primary_image_url ||
              venue?.primaryImageUrl ||
              venue?.images?.[0]?.image_url ||
              '';
            if (!cancelled) setVenueImage(imageUrl);
          } catch {
            if (!cancelled) setVenueImage('');
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [currentUser, userProfile]);

  useEffect(() => {
    if (!pendingBrief) {
      setCountdown(null);
      return undefined;
    }

    const tick = () => {
      setCountdown(computeBriefCountdown(pendingBrief.created_at || pendingBrief.submitted_at));
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [pendingBrief]);

  const featuredBooking = useMemo(
    () =>
      bookings.find((b) => {
        const status = String(b.status || '').toLowerCase();
        return status !== 'cancelled' && status !== 'completed';
      }) || null,
    [bookings]
  );

  const completion = useMemo(
    () =>
      computeProfileCompletion({
        currentUser,
        userProfile,
        hasBookingOrBrief: bookings.length > 0 || Boolean(pendingBrief),
        hasVenueList: false,
      }),
    [currentUser, userProfile, bookings.length, pendingBrief]
  );

  const showBooking = Boolean(featuredBooking);
  const showBrief = !showBooking && Boolean(pendingBrief);
  const showPrimary = loading || showBooking || showBrief;
  const paymentLabel = featuredBooking
    ? getOrganizerPaymentLabel(featuredBooking, canShowOrganizerBookingPricing(featuredBooking))
    : null;

  return (
    <div className="org-home">
      <h1 className="org-page-title">Welcome {firstName},</h1>

      <div className={`org-home-grid ${showPrimary ? '' : 'org-home-grid--profile-only'}`}>
        {showPrimary ? (
          <div className="org-home-primary">
            {loading ? (
              <div className="org-loading">
                <div className="org-loading-spinner" />
                <p>Loading your dashboard...</p>
              </div>
            ) : showBooking ? (
              <FeaturedBookingCard
                booking={featuredBooking}
                venueImage={venueImage}
                paymentLabel={paymentLabel}
              />
            ) : (
              <PendingBriefCard brief={pendingBrief} countdown={countdown} />
            )}
          </div>
        ) : null}

        <OrganizerProfileCompletion completion={completion} />
      </div>
    </div>
  );
};

export default OrganizerHome;
